import React from 'react'
import { ColorResult, SketchPicker } from 'react-color'
import { useTheme } from 'styled-components'

import colors from 'live/services/colors'
import i18n from 'live/services/i18n'

import {
    ButtonContainer,
    ColorButton,
    FormColorPickerToggler,
    Icon,
    PickerContainer,
    PickerPreviewBundle,
    PreviewContainer
} from './styles'

interface Props {
    color: string
    onChange(newColor: string): void
    defaultColor?: string
    caretColor?: string
    style?: React.CSSProperties
}

function propColor(color: string): string {
    return color ?? 'transparent'
}

export function ColorPicker(props: Props): JSX.Element {
    const [show, setShow] = React.useState(false)

    const colorPickerRef = React.useRef<HTMLDivElement>(null)

    function rgba2hex(orig: string): string {
        orig = orig.trim()

        if (!orig.startsWith('rgba')) {
            return orig
        }

        const rgb = orig
            .replace(/\s/g, '')
            .match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i)
            .map(e => Number(e))

        const hex =
            (rgb[1] | (1 << 8)).toString(16).slice(1) +
            (rgb[2] | (1 << 8)).toString(16).slice(1) +
            (rgb[3] | (1 << 8)).toString(16).slice(1)

        if (rgb[4] === 0) {
            return 'transparent'
        }

        return `#${hex}`
    }

    function onChange(color: ColorResult | string): void {
        let newColor: string

        if (typeof color === 'string') {
            newColor = color
        } else {
            const { r, g, b } = color.rgb
            newColor = rgba2hex(`rgba(${r}, ${g}, ${b}, 1)`)
        }

        if (newColor) {
            props.onChange(newColor)
        }
    }

    function normalizeColor(): string {
        if (propColor(props.color) == null || propColor(props.color).trim().length === 0) {
            return null
        }

        return propColor(props.color)
    }

    function resetColor(): void {
        if (props.onChange) {
            props.onChange(props.defaultColor ?? 'transparent')
        }
    }

    React.useEffect(() => {
        function handleMouseDownAction(ev: MouseEvent): void {
            if (colorPickerRef.current && !colorPickerRef.current.contains(ev.target as HTMLElement)) {
                setShow(false)
            }
        }

        document.addEventListener('mousedown', handleMouseDownAction)

        return () => {
            document.removeEventListener('mousedown', handleMouseDownAction)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div
            ref={colorPickerRef}
            className="form-color-picker gs-color-picker dropdown-toggler--cancel-drag"
            style={props.style}
        >
            <FormColorPickerToggler className="form-color-picker__toggler" onClick={() => setShow(old => !old)}>
                <ColorButton>
                    <ButtonContainer>
                        <button
                            style={{
                                background: normalizeColor(),
                                height: 32,
                                width: 32
                            }}
                        />
                    </ButtonContainer>
                </ColorButton>
                <Icon className="color-picker-icon">
                    <i className={`fa fa-pen`} style={{ color: props.caretColor }} />
                </Icon>
            </FormColorPickerToggler>
            {show && (
                <PickerPreviewBundle>
                    <PickerContainer>
                        <SketchPicker
                            color={normalizeColor()}
                            presetColors={colors.all}
                            disableAlpha={true}
                            onChangeComplete={onChange}
                        />

                        <div onClick={resetColor} className="form-color-picker__clear-button">
                            <div className="color" style={{ background: props.defaultColor }} />
                            {i18n('Default Color')}
                        </div>
                    </PickerContainer>
                    <PreviewContainer backgroundColor={normalizeColor() ?? '#dadada'}>
                        <div className="preview-light">
                            <div className="barrier-preview">
                                <div className="barrier-header light">
                                    <div className="header-text light">{i18n('Barrier')}</div>
                                    <div className="header-icons light">
                                        <i className="fas fa-cogs" />
                                        <i className="far fa-copyright" />
                                        <i className="far fa-hand-paper" />
                                    </div>
                                </div>
                                <div className="barrier-content light">{i18n('Barrier light mode')}</div>
                            </div>
                        </div>
                        <div className="preview-dark">
                            <div className="barrier-preview">
                                <div className="barrier-header dark">
                                    <div className="header-text dark">{i18n('Barrier')}</div>
                                    <div className="header-icons dark">
                                        <i className="fas fa-cogs" />
                                        <i className="far fa-copyright" />
                                        <i className="far fa-hand-paper" />
                                    </div>
                                </div>
                                <div className="barrier-content dark">{i18n('Barrier dark mode')}</div>
                            </div>
                        </div>
                    </PreviewContainer>
                </PickerPreviewBundle>
            )}
        </div>
    )
}
